import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from 'react-oauth2-code-pkce';
import GlobalContext from '../context/GlobalContext';
import CompaniesService from '../services/companies-service';
import AdminShell from './AdminShell';
import DeniedCard from './DeniedCard';
import { IconTrash } from './Icons';

export default function CompanyListPage() {
  const { currentUser, userLoading } = useContext(GlobalContext);
  const { token } = useContext(AuthContext);
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '' });
  const [saving, setSaving] = useState(false);

  // Fetch companies on component mount
  useEffect(() => {
    if (token && currentUser?.isAdmin) {
      fetchCompanies();
    }
  }, [token, currentUser]);

  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const companiesData = await CompaniesService.getAllCompanies(token);
      setCompanies(companiesData);
    } catch (err) {
      setError('Fout bij laden van bedrijven: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.name.trim()) {
      setError('Bedrijfsnaam is verplicht');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await CompaniesService.createCompany({ name: newCompany.name.trim() }, token);
      await fetchCompanies();
      setIsCreating(false);
      setNewCompany({ name: '' });
      setSuccessMessage('Bedrijf succesvol aangemaakt!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Fout bij aanmaken van bedrijf: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Weet je zeker dat je het bedrijf "${companyName}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) {
      return;
    }

    try {
      await CompaniesService.deleteCompany(companyId, token);
      await fetchCompanies();
      setSuccessMessage('Bedrijf succesvol verwijderd!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Fout bij verwijderen van bedrijf: ' + (err.message || err));
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setNewCompany({ name: '' });
    setError(null);
  };

  if (userLoading) {
    return (
      <div className="page-shell flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentUser?.isAdmin) {
    return <DeniedCard />;
  }

  return (
    <AdminShell
      title="Bedrijvenbeheer"
      backHref="/beheer"
      backLabel="Beheer"
      actions={(
        <>
          <button type="button" onClick={() => setIsCreating(true)} disabled={loading} className="btn-primary">
            Nieuw bedrijf
          </button>
          <button type="button" onClick={fetchCompanies} disabled={loading} className="btn-ghost">
            Vernieuwen
          </button>
        </>
      )}
    >
        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {successMessage}
          </div>
        )}

        {/* Companies table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Bedrijven laden...</span>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12">
              <span className="material-icons-outlined text-gray-400 text-6xl mb-4">business</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Geen bedrijven gevonden</h3>
              <p className="text-gray-600 mb-6">Er zijn nog geen bedrijven aangemaakt.</p>
              <button
                onClick={() => setIsCreating(true)}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                <span className="material-icons-outlined mr-2 text-sm">add</span>
                Eerste bedrijf aanmaken
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Naam
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aangemaakt
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="material-icons-outlined text-gray-400 mr-3">business</span>
                          <div className="text-sm font-medium text-gray-900">{company.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.createdAt 
                          ? new Date(company.createdAt).toLocaleDateString('nl-NL')
                          : 'Onbekend'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <a
                            href={`/bedrijven/${company.id}`}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                          >
                            Details
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteCompany(company.id, company.name)}
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 dark:hover:text-red-400 transition-colors"
                          >
                            <IconTrash size={16} />
                            Verwijderen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary stats */}
        {!loading && companies.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card p-6">
              <div className="text-2xl font-bold text-blue-600">{companies.length}</div>
              <div className="text-sm text-gray-600">Totaal bedrijven</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-green-600">
                {companies.filter(c => c.createdAt && new Date(c.createdAt).getFullYear() === new Date().getFullYear()).length}
              </div>
              <div className="text-sm text-gray-600">Dit jaar aangemaakt</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-purple-600">
                {companies.filter(c => c.creatorUserId).length}
              </div>
              <div className="text-sm text-gray-600">Met eigenaar</div>
            </div>
          </div>
        )}

      {/* Create Company Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Nieuw bedrijf aanmaken</h3>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                >
                  <span className="material-icons-outlined">close</span>
                </button>
              </div>
            </div>

            <div className="px-6 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bedrijfsnaam *
                </label>
                <input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({ name: e.target.value })}
                  disabled={saving}
                  placeholder="Voer bedrijfsnaam in..."
                  className="input-field"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="btn-ghost"
              >
                Annuleren
              </button>
              <button
                onClick={handleCreateCompany}
                disabled={saving || !newCompany.name.trim()}
                className="btn-primary flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Aanmaken...
                  </>
                ) : (
                  <>
                    <span className="material-icons-outlined mr-2 text-sm">add</span>
                    Aanmaken
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
